import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { bienestarService } from '../services/api';
import './BienestarReservarPage.css';

function readQuery() {
  const params = new URLSearchParams(window.location.search);
  return {
    ref: params.get('ref') || params.get('bookingRef') || '',
    email: params.get('email') || '',
  };
}

function formatScheduledLabel(isoLocal, locale) {
  if (!isoLocal) return '';
  const d = new Date(isoLocal.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return isoLocal;
  return d.toLocaleString(locale === 'en' ? 'en-CO' : 'es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Bogota',
  });
}

const BienestarReprogramarPage = () => {
  const { t, i18n } = useTranslation();
  const initial = useMemo(() => readQuery(), []);

  const [step, setStep] = useState(1);
  const [bookingRef, setBookingRef] = useState(initial.ref);
  const [email, setEmail] = useState(initial.email);
  const [booking, setBooking] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const daySlots = useMemo(() => {
    const day = availability.find((d) => d.date === selectedDay);
    return day?.slots?.filter((s) => s.available) || [];
  }, [availability, selectedDay]);

  const loadAvailability = useCallback(async (slug, ref, mail) => {
    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.getAvailability(slug, null, null, {
        bookingRef: ref,
        email: mail,
      });
      const data = res?.data || res;
      const days = data?.days || [];
      setAvailability(days);
      if (days.length > 0) {
        setSelectedDay(days[0].date);
      }
    } catch {
      setError(t('bienestarReschedule.errorGeneric'));
      setAvailability([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const goBienestar = useCallback(() => {
    window.history.pushState({}, '', '/bienestar');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    const ref = bookingRef.trim();
    const mail = email.trim().toLowerCase();
    if (!ref || !mail) return;

    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.getBooking(ref, mail);
      const data = res?.data || res;
      const b = data?.booking;
      if (!b) {
        setError(t('bienestarReschedule.notFound'));
        return;
      }
      setBooking(b);
      if (b.scheduleStatus !== 'scheduled' || !b.scheduledAt) {
        setError(t('bienestarReschedule.noScheduleYet'));
        return;
      }
      if (!b.canReschedule) {
        setError(t('bienestarReschedule.tooLate'));
        return;
      }
      setStep(2);
      await loadAvailability(b.serviceSlug, ref, mail);
    } catch (err) {
      setError(err.response?.data?.message || t('bienestarReschedule.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !booking) return;

    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.rescheduleBooking({
        bookingRef: booking.bookingRef,
        email: booking.email,
        slotStart: selectedSlot,
      });
      const data = res?.data || res;
      setScheduledAt(data?.booking?.scheduledAt || selectedSlot);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || t('bienestarReschedule.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const scheduledLabel = formatScheduledLabel(scheduledAt, i18n.language);

  return (
    <div className="br-page">
      <Header />
      <main className="br-main">
        <button type="button" className="br-back" onClick={goBienestar}>
          <i className="fas fa-arrow-left" aria-hidden="true" /> {t('bienestarReschedule.back')}
        </button>

        <p className="br-kicker">{t('bienestarReschedule.kicker')}</p>
        <h1 className="br-title">{t('bienestarReschedule.title')}</h1>
        <p className="br-lead">{t('bienestarReschedule.lead')}</p>

        {step === 1 && (
          <form className="br-card" onSubmit={handleLookup}>
            <h2>{t('bienestarReschedule.step1Title')}</h2>
            <p className="br-hint">{t('bienestarReschedule.step1Hint')}</p>
            <label className="br-label">
              {t('bienestarBooking.refLabel')}
              <input
                type="text"
                className="br-input"
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value)}
                placeholder="BW-XXXXXXXX"
                required
              />
            </label>
            <label className="br-label">
              {t('bienestarBooking.emailLabel')}
              <input
                type="email"
                className="br-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            {error && <p className="br-error" role="alert">{error}</p>}
            <button type="submit" className="br-cta" disabled={loading}>
              {loading ? '...' : t('bienestarReschedule.step1Cta')}
            </button>
          </form>
        )}

        {step === 2 && booking && (
          <form className="br-card" onSubmit={handleReschedule}>
            <h2>{t('bienestarReschedule.step2Title')}</h2>
            <p className="br-hint">
              {t('bienestarReschedule.currentSchedule')}:{' '}
              <strong>{formatScheduledLabel(booking.scheduledAt, i18n.language)}</strong>
            </p>
            <p className="br-hint">{t('bienestarBooking.step3Hint')}</p>

            {loading && availability.length === 0 ? (
              <p className="br-note">{t('bienestarBooking.loadingSlots')}</p>
            ) : availability.length === 0 ? (
              <p className="br-note">{t('bienestarBooking.noSlots')}</p>
            ) : (
              <>
                <label className="br-label">
                  {t('bienestarBooking.pickDay')}
                  <select
                    className="br-input"
                    value={selectedDay}
                    onChange={(e) => {
                      setSelectedDay(e.target.value);
                      setSelectedSlot('');
                    }}
                  >
                    {availability.map((d) => (
                      <option key={d.date} value={d.date}>{d.label}</option>
                    ))}
                  </select>
                </label>
                <div className="br-slots">
                  {daySlots.map((slot) => (
                    <button
                      key={slot.start}
                      type="button"
                      className={selectedSlot === slot.start ? 'br-slot br-slot--on' : 'br-slot'}
                      onClick={() => setSelectedSlot(slot.start)}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
                {daySlots.length === 0 && selectedDay && (
                  <p className="br-note">{t('bienestarBooking.noSlotsDay')}</p>
                )}
              </>
            )}

            {error && <p className="br-error" role="alert">{error}</p>}
            <button type="submit" className="br-cta" disabled={loading || !selectedSlot}>
              {loading ? '...' : t('bienestarReschedule.step2Cta')}
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="br-card br-success-card">
            <i className="fas fa-check-circle br-success-icon" aria-hidden="true" />
            <h2>{t('bienestarReschedule.successTitle')}</h2>
            {scheduledLabel && (
              <p className="br-scheduled">
                <i className="fas fa-calendar-check" aria-hidden="true" /> {scheduledLabel}
              </p>
            )}
            <p>{t('bienestarReschedule.successBody')}</p>
            <button type="button" className="br-cta" onClick={goBienestar}>
              {t('bienestarBooking.backBienestar')}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default BienestarReprogramarPage;
